import React from "react";
import {
  FiChevronDown,
} from "react-icons/fi";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import { LuCheckCircle, LuLock, LuShield, LuUsers } from "react-icons/lu";
import {
  AccessLevelName,
  accessLevelClassName,
  accessLevelDisplayName,
  accessMatrix,
  roleRows,
} from "./PayoutAccessControlData";
import PayoutDataTable from "./PayoutDataTable";
import PayoutTableCell from "./PayoutTableCell";
import { payoutApi } from "./api/payoutApiClient";
import { apiNumber, roleFromApiRecord } from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";

const roleStats = [
  {
    label: "Total Roles",
    value: "4",
    helper: "One role mapped to each access level",
    helperClass: "text-[#505E8E]",
    icon: <LuUsers />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Active Roles",
    value: "4",
    helper: "All predefined roles enabled",
    helperClass: "text-[#505E8E]",
    icon: <LuCheckCircle />,
    tone: "bg-[#16A34A1A] text-[#16A34A]",
  },
  {
    label: "Access Levels",
    value: "4",
    helper: "Level 1 through Level 4",
    helperClass: "text-[#505E8E]",
    icon: <LuLock />,
    tone: "bg-[#F59E0B1A] text-[#F97316]",
  },
  {
    label: "Assigned Users",
    value: "49",
    helper: "Across all payout roles",
    helperClass: "text-[#505E8E]",
    icon: <LuShield />,
    tone: "bg-[#E0E7FF] text-[#3730A3]",
  },
];

const statusClassName: Record<string, string> = {
  Active: "bg-[#DCFCE7] text-[#16A34A]",
  Inactive: "bg-[#E5E7EB] text-[#505E8E]",
};

const emptyRoleRow: (typeof roleRows)[number] = {
  slug: "",
  name: "",
  type: "",
  accessLevel: "Observer",
  description: "",
  users: 0,
  status: "",
  deletable: false,
};

const fallbackRoleRows: typeof roleRows = roleRows.map((role) => ({
  ...role,
  users: 0,
}));

const matrixFromApiPayload = (payload: Record<string, unknown>) => {
  const rawRows =
    payload.matrix ||
    payload.access_matrix ||
    payload.accessMatrix ||
    payload.rows ||
    payload.items ||
    [];

  if (!Array.isArray(rawRows)) return [];

  return rawRows
    .map((row) => {
      if (Array.isArray(row)) return row.map(String);
      if (!row || typeof row !== "object") return null;
      const record = row as Record<string, unknown>;
      return [
        record.module || record.feature || record.name,
        record.level_1 || record.level1 || record.observer,
        record.level_2 || record.level2 || record.finance_executive,
        record.level_3 || record.level3 || record.finance_manager,
        record.level_4 || record.level4 || record.administrator,
      ].map((cell) => String(cell || "-"));
    })
    .filter((row): row is string[] => Boolean(row && row[0]));
};

function PayoutRoles() {
  const rolesResource = usePayoutResource(
    [] as typeof roleRows,
    (signal) =>
      payoutApi.roles({}, signal).then((rows) =>
        rows.map((row) => roleFromApiRecord(row, emptyRoleRow)),
      ),
  );
  const summaryResource = usePayoutResource<Record<string, unknown> | null>(
    null,
    (signal) => payoutApi.rolesSummary(signal),
  );
  const accessMatrixResource = usePayoutResource(
    [] as typeof accessMatrix,
    (signal) =>
      payoutApi.rolesAccessMatrix(signal).then((payload) =>
        matrixFromApiPayload(payload),
      ),
  );
  const filteredRoleRows =
    rolesResource.data.length > 0 ? rolesResource.data : fallbackRoleRows;
  const summary = summaryResource.data || {};
  const displayedAccessMatrix =
    accessMatrixResource.data.length > 0 ? accessMatrixResource.data : accessMatrix;
  const displayedRoleStats = [
          {
            ...roleStats[0],
            value: String(
              apiNumber(
                summary,
                ["total_roles", "totalRoles", "total", "count"],
                filteredRoleRows.length,
              ),
            ),
            helper: "From payouts-service",
          },
          {
            ...roleStats[1],
            value: String(
              apiNumber(
                summary,
                ["active_roles", "activeRoles", "active"],
                filteredRoleRows.filter((role) => role.status === "Active").length,
              ),
            ),
          },
          {
            ...roleStats[2],
            value: String(
              apiNumber(
                summary,
                ["access_levels", "accessLevels"],
                new Set(filteredRoleRows.map((role) => role.accessLevel).filter(Boolean)).size,
              ),
            ),
          },
          {
            ...roleStats[3],
            value: String(
              apiNumber(
                summary,
                ["assigned_users", "assignedUsers", "user_count", "userCount"],
                filteredRoleRows.reduce((total, role) => total + Number(role.users || 0), 0),
              ),
            ),
          },
        ];
  const roleTableRows = filteredRoleRows.map((role) => {
    const accessLevel = role.accessLevel as AccessLevelName;
    return [
    <span>
      <PayoutTableCell
        value={role.name}
        valueClassName="font-semibold text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={`${role.type} role`}>
        {role.type} role
      </span>
    </span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        accessLevelClassName[accessLevel]
      }
    >
      {accessLevelDisplayName[accessLevel]}
    </span>,
    <PayoutTableCell
      value={role.description}
      valueClassName="font-medium leading-5 text-[#1B163B]"
      maxWidthClassName="max-w-[320px]"
    />,
    <span className="font-semibold">{role.users}</span>,
    <span
      className={
        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
        statusClassName[role.status]
      }
    >
      {role.status}
    </span>,
  ];
  });
  const accessMatrixRows = displayedAccessMatrix.map((row) =>
    row.map((cell, index) => (
      <span
        className={
          "font-semibold " +
          (index === 0
            ? "text-[#1B163B]"
            : cell === "No"
              ? "text-[#A3AED0]"
              : "text-[#16A34A]")
        }
      >
        {cell}
      </span>
    )),
  );

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Roles
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Manage reusable access templates based on 4 predefined access levels.
          </p>
        </div>
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-4">
        {displayedRoleStats.map((stat) => (
          <div
            key={stat.label}
            className="flex min-h-[118px] items-center gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-6 py-5 new-shadow"
          >
            <div
              className={
                "payout-kpi-icon flex h-11 w-11 shrink-0 items-center justify-center text-xl " +
                stat.tone
              }
            >
              {stat.icon}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#505E8E]">
                {stat.label}
              </p>
              <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
                {stat.value}
              </p>
              <p className={"mt-4 text-xs font-semibold " + stat.helperClass}>
                {stat.helper}
              </p>
            </div>
          </div>
        ))}
      </div>

      <PayoutDataTable
        columns={[
          "Role Name",
          "Access Level",
          "Description",
          "Users Assigned",
          "Status",
        ]}
        rows={roleTableRows}
        minWidth="min-w-[900px]"
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing 1 to {filteredRoleRows.length} of {filteredRoleRows.length} roles
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <button className="flex h-9 min-w-[120px] items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B]">
                10 per page
                <FiChevronDown />
              </button>
              <div className="flex items-center gap-2">
                <button className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E]">
                  <FaArrowLeft className="text-xs" />
                </button>
                <button className="h-9 w-9 rounded-md border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]">
                  1
                </button>
                <button className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E]">
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <section className="hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1060px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="px-5 py-4 text-center">Role Name</th>
                <th className="px-5 py-4 text-center">Access Level</th>
                <th className="px-5 py-4 text-center">Description</th>
                <th className="px-5 py-4 text-center">Users Assigned</th>
                <th className="px-5 py-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredRoleRows.map((role) => {
                const accessLevel = role.accessLevel as AccessLevelName;
                return (
                  <tr
                    key={role.slug}
                    className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B] hover:bg-[#A9B2CF1A]"
                  >
                  <td className="px-5 py-4">
                    <span className="font-semibold text-[#1B163B]">
                      {role.name}
                    </span>
                    <p className="mt-1 text-[#505E8E]">{role.type} role</p>
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        accessLevelClassName[accessLevel]
                      }
                    >
                      {accessLevelDisplayName[accessLevel]}
                    </span>
                  </td>
                  <td className="max-w-[420px] px-5 py-4 leading-5">
                    {role.description}
                  </td>
                  <td className="px-5 py-4 font-semibold">{role.users}</td>
                  <td className="px-5 py-4">
                    <span
                      className={
                        "rounded-[5px] px-2 py-1 text-xs font-semibold " +
                        statusClassName[role.status]
                      }
                    >
                      {role.status}
                    </span>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
          <span className="min-w-max">Showing 1 to {filteredRoleRows.length} of {filteredRoleRows.length} roles</span>
          <div className="ml-auto flex min-w-max items-center gap-4">
            <button className="flex h-9 min-w-[120px] items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B]">
              10 per page
              <FiChevronDown />
            </button>
            <div className="flex items-center gap-2">
              <button className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E]">
                <FaArrowLeft className="text-xs" />
              </button>
              <button className="h-9 w-9 rounded-md border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]">
                1
              </button>
              <button className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E]">
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <PayoutDataTable
        title="Access Matrix"
        columns={["Module", "Level 1", "Level 2", "Level 3", "Level 4"]}
        rows={accessMatrixRows}
        minWidth="min-w-[900px]"
      />

      <section className="hidden">
        <div className="border-b border-[#E0E5F2] px-5 py-4">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Access Matrix
          </h2>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Higher access levels automatically inherit every permission from lower levels.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="px-5 py-4 text-center">Module</th>
                <th className="px-5 py-4 text-center">Level 1</th>
                <th className="px-5 py-4 text-center">Level 2</th>
                <th className="px-5 py-4 text-center">Level 3</th>
                <th className="px-5 py-4 text-center">Level 4</th>
              </tr>
            </thead>
            <tbody>
              {displayedAccessMatrix.map((row) => (
                <tr key={row[0]} className="border-t border-[#E0E5F2] text-xs font-semibold text-[#1B163B]">
                  {row.map((cell, index) => (
                    <td key={cell + index} className="px-5 py-4">
                      <span
                        className={
                          index === 0
                            ? ""
                            : cell === "No"
                              ? "text-[#A3AED0]"
                              : "text-[#16A34A]"
                        }
                      >
                        {cell}
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

    </div>
  );
}

export default PayoutRoles;
